/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   AlertTimeline.tsx
 *     Feature         :   117-operational-dashboards
 *     Description     :   Timeline of SLA breaches color-coded by severity
 *     Constitution    :   v1.12.0 | Enterprise Dependency Architecture
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-05
 * =========================================================================================
 */

import React from 'react';
import clsx from 'clsx';
import { formatEastern } from '@/utils/dateFormatter';
import { AlertTriangle, AlertCircle, Info } from 'lucide-react';
import type { SlaAlertRow } from '../../services/operations/slaService';

interface AlertTimelineProps {
  alerts: SlaAlertRow[];
}

const SEVERITY_CONFIG: Record<string, { icon: React.ReactNode; bg: string; border: string; text: string }> = {
  Critical: {
    icon: <AlertTriangle className="h-4 w-4 text-red-500" />,
    bg: 'bg-red-50 dark:bg-red-950',
    border: 'border-red-300 dark:border-red-700',
    text: 'text-red-800 dark:text-red-200',
  },
  Warning: {
    icon: <AlertCircle className="h-4 w-4 text-yellow-500" />,
    bg: 'bg-yellow-50 dark:bg-yellow-950',
    border: 'border-yellow-300 dark:border-yellow-700',
    text: 'text-yellow-800 dark:text-yellow-200',
  },
  Info: {
    icon: <Info className="h-4 w-4 text-blue-500" />,
    bg: 'bg-blue-50 dark:bg-blue-950',
    border: 'border-blue-300 dark:border-blue-700',
    text: 'text-blue-800 dark:text-blue-200',
  },
};

export const AlertTimeline: React.FC<AlertTimelineProps> = ({ alerts }) => {
  if (!alerts || alerts.length === 0) {
    return <div className="text-[var(--color-text-secondary)] py-4 text-center">No open capacity alerts</div>;
  }

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto">
      {alerts.map((alert) => {
        const config = SEVERITY_CONFIG[alert.severity] ?? SEVERITY_CONFIG.Info;
        return (
          <div
            key={alert.id}
            className={clsx('flex items-start gap-3 p-3 rounded-lg border', config.bg, config.border)}
          >
            <div className="mt-0.5">{config.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className={clsx('text-sm font-medium', config.text)}>
                  {alert.capacity_name}
                </span>
                <span className="text-xs text-[var(--color-text-secondary)]">
                  {formatEastern(alert.occurred_at)}
                </span>
              </div>
              <p className="text-sm text-[var(--color-text-secondary)] mt-0.5">{alert.breach_description}</p>
              <span className={clsx('inline-flex px-1.5 py-0.5 rounded text-xs font-medium mt-1', config.text)}>
                {alert.capacity_type} - {alert.severity}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
